/* eslint-disable no-undef */
class AddonsModal extends HTMLElement {
  constructor () {
    super()
    this.triggerProductId = null
    this.triggerVariantId = null
    this.triggerProductCartResponse = null
    this.selectedProducts = new Map()
    this.isProcessing = false
  }

  connectedCallback () {
    this.setupElements()
    this.setupListeners()
  }

  setupElements () {
    this.overlay = this.querySelector('.addons-modal-container')
    this.skipButton = this.querySelector('.addons-modal__skip-button')
    this.skipButtonSpinner = this.querySelector('.addons-modal__skip-button-spinner-wrapper')
    this.skipButtonText = this.querySelector('.addons-modal__skip-button-text')
    this.skipToCartButton = this.querySelector('.addons-modal__skip-to-cart-button')
    this.skipToCartButtonSpinner = this.querySelector('.addons-modal__skip-to-cart-button-spinner')
    this.skipToCartButtonText = this.querySelector('.addons-modal__skip-to-cart-button-text')
    this.productsGrid = this.querySelector('.addons-modal__products-grid')
    this.footer = this.querySelector('.addons-modal__footer')
    this.addButton = this.querySelector('.addons-modal__add-button')
    this.addButtonSpinner = this.querySelector('.addons-modal__add-button-spinner')
    this.addButtonText = this.querySelector('.addons-modal__add-button-text')
    this.bodyWrapper = this.querySelector('.addons-modal__body-wrapper')
    this.fader = this.querySelector('.addons-modal__body-fader')
    this.bottomSentinel = this.querySelector('.addons-modal__bottom-sentinel')
    this.toast = document.querySelector('.addons-modal__toast')
    this.toastText = this.toast ? this.toast.querySelector('.addons-modal__toast-text') : null
    this.toastTimer = null
    this.variantOverlay = null
  }

  setupListeners () {
    window.addEventListener('cart:added-with-modal', this.handleCartAddedWithModal.bind(this))

    this.skipButton.addEventListener('click', () => {
      this.skipToCart()
    })

    this.skipToCartButton.addEventListener('click', () => {
      this.skipToCart()
    })

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.skipToCart()
      }
    })

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || this.getAttribute('open') !== 'true') return
      const reusableModal = document.querySelector('reusable-modal')
      if (reusableModal && reusableModal.getAttribute('open') === 'true') return
      this.skipToCart()
    })

    // Ensure listeners are not duplicated across openings
    this.addButton.addEventListener('click', () => {
      this.addSelectedProductsToCart()
    })

    window.addEventListener('addons-modal:variant-modal-closed', () => {
      this.hideVariantBackdrop()
    })
  }

  handleCartAddedWithModal (event) {
    const { cartResponse, variantId, productId } = event.detail || {}

    if (!productId || !variantId || !cartResponse) return

    this.triggerProductId = productId
    this.triggerVariantId = variantId
    this.triggerProductCartResponse = cartResponse

    this.openModal()
    this.showToast(cartResponse)
  }

  async openModal () {
    this.setAttribute('open', 'true')
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))

    this.resetModal()
    this.initScrollFader()
    await this.renderProductsFromDOM()
  }

  closeModal () {
    this.setAttribute('open', 'false')
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    window.dispatchEvent(new CustomEvent('addons-modal:closed'))
    this.cleanupScrollFader()
    this.hideToast()
    this.hideVariantBackdrop()
  }

  showVariantBackdrop () {
    if (!this.variantOverlay) {
      this.variantOverlay = document.createElement('div')
      this.variantOverlay.className = 'addons-modal__variant-overlay'
      this.variantOverlay.setAttribute('aria-hidden', 'true')
      this.variantOverlay.innerHTML = '<span class="sr-only">Overlay</span>'
    }

    if (!this.variantOverlay.isConnected) {
      document.body.appendChild(this.variantOverlay)
    }

    requestAnimationFrame(() => {
      this.variantOverlay.classList.add('addons-modal__variant-overlay--visible')
    })
  }

  hideVariantBackdrop () {
    if (!this.variantOverlay) return
    this.variantOverlay.classList.remove('addons-modal__variant-overlay--visible')
    setTimeout(() => {
      if (
        this.variantOverlay &&
        !this.variantOverlay.classList.contains('addons-modal__variant-overlay--visible')
      ) {
        this.variantOverlay.remove()
      }
    }, 250)
  }

  showToast (cartResponse) {
    if (!this.toast || !this.toastText) return

    const template = window.TentBox?.addonsModal?.toastText || ''
    if (!template) return

    let productTitle = ''
    if (cartResponse) {
      productTitle = cartResponse.product_title || cartResponse.title || ''
    }
    const modelTitle = productTitle.replace(/^TentBox\s+/, '')
    const message = template
      .replace('[product]', productTitle)
      .replace('[model]', modelTitle)

    this.toastText.textContent = message
    this.toast.hidden = false

    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }

    requestAnimationFrame(() => {
      this.toast.classList.remove('opacity-0', '-translate-y-2')
      this.toast.classList.add('opacity-100', 'translate-y-0')
    })

    const duration = window.TentBox?.addonsModal?.toastDuration || 5000
    this.toastTimer = setTimeout(() => {
      this.hideToast()
    }, duration)
  }

  hideToast () {
    if (!this.toast) return
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
    this.toast.classList.remove('opacity-100', 'translate-y-0')
    this.toast.classList.add('opacity-0', '-translate-y-2')
    setTimeout(() => {
      if (this.toast && this.toast.classList.contains('opacity-0')) {
        this.toast.hidden = true
      }
    }, 220)
  }

  resetModal () {
    this.selectedProducts.clear()
    this.footer.classList.remove('hidden')
    this.addButton.removeAttribute('loading')
    this.addButton.disabled = true
    this.addButton.classList.remove('addons-modal__add-button--active')
    this.setSkipButtonLoading(false)
    this.setSkipToCartButtonLoading(false)
    // Clear any previous selection state on cards
    if (this.productsGrid) {
      const cards = this.productsGrid.querySelectorAll('.addons-modal__card')
      cards.forEach(card => {
        card.dataset.selected = 'false'
        this.updateAddButtonText(card, false)
      })
    }
    this.updateFooter()

    // Reset fader
    if (this.fader) {
      this.fader.classList.remove('show')
    }
  }

  normalizeAddonsProductData (raw) {
    if (!raw) return raw
    const filter = raw.modelFilter || {}
    const allVariants = (raw.allVariants || raw.variants || []).filter(v => v && v.available !== false)

    const labelPos = filter.labelOptionPosition
    const withPickerLabel = (variant) => {
      let pickerLabel = variant.title
      if (filter.applyModelFilter && labelPos) {
        if (labelPos === 1) pickerLabel = variant.option1 || pickerLabel
        else if (labelPos === 2) pickerLabel = variant.option2 || pickerLabel
        else if (labelPos === 3) pickerLabel = variant.option3 || pickerLabel
      }
      return { ...variant, pickerLabel }
    }

    let variants = allVariants.map(withPickerLabel)

    if (filter.applyModelFilter && Array.isArray(filter.matchedVariantIds) && filter.matchedVariantIds.length > 0) {
      const idSet = new Set(filter.matchedVariantIds.map(id => Number(id)))
      variants = variants.filter(v => idSet.has(Number(v.id)))
    }

    if (filter.collapseNoMatch && variants.length === 0) {
      variants = allVariants.map(withPickerLabel)
    }

    const firstId = filter.firstVariantId !== null && filter.firstVariantId !== undefined
      ? Number(filter.firstVariantId)
      : Number(variants[0]?.id)
    const selectedVariant = variants.find(v => Number(v.id) === firstId) || variants[0]

    const hasMultipleVariants = typeof filter.hasMultipleVariants === 'boolean'
      ? filter.hasMultipleVariants
      : variants.length > 1

    return {
      ...raw,
      allVariants,
      variants,
      variantTypeLabel: filter.variantTypeLabel || raw.variantTypeLabel || 'option',
      hasMultipleVariants,
      selectedVariant: selectedVariant
        ? {
            id: selectedVariant.id,
            price: selectedVariant.price,
            compareAtPrice: selectedVariant.compare_at_price ?? selectedVariant.compareAtPrice ?? 0,
          }
        : raw.selectedVariant,
    }
  }

  async renderProductsFromDOM () {
    this.footer.classList.remove('hidden')
    // Remove items already in the cart
    const cartItems = await this.getCartItems().catch(() => [])
    const cartProductIds = new Set(cartItems.map(item => item.product_id))

    let cards = Array.from(this.productsGrid.querySelectorAll('.addons-modal__card'))
    cards.forEach(card => {
      const productId = parseInt(card.dataset.productId)
      if (cartProductIds.has(productId)) {
        const next = card.nextElementSibling
        if (next && next.tagName === 'SCRIPT' && next.dataset && 'productData' in next.dataset) {
          next.remove()
        }
        card.remove()
      }
    })

    // Remove items with no available variants
    cards = Array.from(this.productsGrid.querySelectorAll('.addons-modal__card'))
    cards.forEach(card => {
      const next = card.nextElementSibling
      if (next && next.tagName === 'SCRIPT' && next.dataset && 'productData' in next.dataset) {
        try {
          const productData = this.normalizeAddonsProductData(JSON.parse(next.textContent))
          if (!productData.available || !productData.selectedVariant?.id) {
            card.remove()
            next.remove()
          }
        } catch (e) {
          card.remove()
          next.remove()
        }
      }
    })

    cards = Array.from(this.productsGrid.querySelectorAll('.addons-modal__card'))
    if (cards.length === 0) {
      this.skipAndAddTriggerProduct()
      return
    }

    cards.forEach(card => {
      let dataScript = null
      const cursor = card.nextElementSibling
      if (cursor && cursor.tagName === 'SCRIPT' && cursor.dataset && 'productData' in cursor.dataset) {
        dataScript = cursor
      } else {
        dataScript = card.querySelector('script[data-product-data]')
        if (!dataScript) {
          const scripts = Array.from(this.productsGrid.querySelectorAll('script[data-product-data]'))
          const cardIndex = Array.from(this.productsGrid.children).indexOf(card)
          dataScript = scripts.find(s => Array.from(this.productsGrid.children).indexOf(s) === cardIndex + 1) || scripts[0]
        }
      }
      if (!dataScript) return
      try {
        const productData = this.normalizeAddonsProductData(JSON.parse(dataScript.textContent))
        this.attachCardListeners(card, productData)
        if (productData.selectedVariant?.price) {
          this.updateFinanceLabel(card, productData.selectedVariant.price)
        }
      } catch (e) {}
    })
    setTimeout(() => {
      this.updateScrollFader()
    }, 10)
  }

  async getCartItems () {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' })
      if (!response.ok) return []
      const cart = await response.json()
      return cart.items || []
    } catch (e) {
      return []
    }
  }

  attachCardListeners (card, productData) {
    // Remove existing listeners by cloning (ensures reusability across openings)
    const cloned = card.cloneNode(true)
    card.parentNode.replaceChild(cloned, card)
    card = cloned
    card.addEventListener('click', () => {
      if (productData.hasMultipleVariants) {
        if (card.dataset.selected === 'true') {
          this.toggleProductSelection(productData, card)
        } else {
          this.openAddonVariantModal(card, productData)
        }
        return
      }
      this.toggleProductSelection(productData, card)
    })

    // Prevent view link from selecting the product
    // No view link in simplified modal
  }

  updateFinanceLabel (card, priceInCents) {
    const financeLabel = card.querySelector('[data-finance]')
    if (!financeLabel) return

    if (priceInCents < 5000) {
      financeLabel.style.display = 'none'
      return
    }

    const finance = this.calculateFinance(priceInCents)
    if (finance) {
      const fromText = window.TentBox?.addonsModal?.financeFromText || 'From'
      const perMonthText = window.TentBox?.addonsModal?.financePerMonthText || '/mo'
      financeLabel.textContent = `${fromText} ${finance} ${perMonthText}`
      financeLabel.style.display = 'inline-flex'
    } else {
      financeLabel.style.display = 'none'
    }
  }

  escapeHtml (str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  buildAddonVariantPickerRows (variants, productTitle) {
    return variants.map((v) => {
      const rawTitle = v.pickerLabel || (v.title === 'Default Title' ? productTitle : v.title)
      const safeTitle = this.escapeHtml(rawTitle || '')
      const priceStr = this.formatPrice(v.price)
      const cap = v.compare_at_price ?? v.compareAtPrice ?? 0
      const hasDiscount = cap > v.price
      const compareBlock = hasDiscount
        ? `<span class="price-display-wrapper__compare line-through font-normal text-alizarin text-[15px] leading-6 shrink-0">${this.formatPrice(cap)}</span>`
        : ''
      const unavailable = !v.available
      return `
      <button type="button"
        class="addons-modal__variant-picker-row cart-cross-sell-modal__variant-button flex rounded border border-clay items-center justify-between gap-2 py-2 px-3 hover:bg-oat transition-colors text-left w-full${unavailable ? ' opacity-50 cursor-not-allowed' : ''}"
        data-variant-id="${v.id}"
        ${unavailable ? 'disabled' : ''}>
        <span class="addons-modal__variant-picker-row__title cart-cross-sell-modal__variant-title text-sm font-body flex-grow font-normal pointer-events-none">${safeTitle}</span>
        <span class="addons-modal__variant-picker-row__prices flex items-center gap-1.5 shrink-0 pointer-events-none">
          <span class="price-display-wrapper__current text-sm font-medium">${priceStr}</span>
          ${compareBlock}
        </span>
      </button>
    `
    }).join('')
  }

  openAddonVariantModal (card, productData) {
    const variants = productData.variants || []
    if (variants.length === 0) {
      this.toggleProductSelection(productData, card)
      return
    }

    const variantType = productData.variantTypeLabel || 'option'
    const instructionRaw = String(window.Tentbox?.variantSelection?.instruction || '')
      .replace('{variantType}', variantType)
      .replace('{productTitle}', productData.title || '')
    const instruction = instructionRaw
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')

    const rowsHtml = this.buildAddonVariantPickerRows(variants, productData.title || '')

    this.showVariantBackdrop()

    window.dispatchEvent(new CustomEvent('open:modal', {
      detail: {
        width: '500px',
        padding: 'none',
        headerClassAdd: ['pt-2', 'px-4', 'block'],
        headerClassRemove: ['grid'],
        bodyClassAdd: ['relative', 'z-10'],
        footerClassAdd: ['p-4'],
        footerClassRemove: ['hidden', 'pb-4', 'md:pb-0', 'mt-4'],
        closeClassAdd: ['absolute', 'top-2', 'right-2', 'z-20'],
        showFooter: true,
        onCloseEvent: 'addons-modal:variant-modal-closed',
      },
    }))

    window.dispatchEvent(new CustomEvent('hide:page-obscure'))

    const modal = document.querySelector('reusable-modal')
    const modalTitle = modal?.querySelector('.reusable-modal__title')
    if (modalTitle && window.Tentbox?.variantSelection?.title) {
      modalTitle.textContent = window.Tentbox.variantSelection.title
    }

    const modalBody = modal?.querySelector('.reusable-modal__body')
    if (modalBody) {
      modalBody.innerHTML = `
      <style>
        .addons-modal__variant-picker-row:focus-visible {
          outline: 2px solid #ed8008;
          outline-offset: 2px;
        }
        .addons-modal__variant-picker-row--selected {
          border-color: #000;
          background-color: rgb(250 250 249);
        }
      </style>
      <div class="addons-modal__variant-picker p-4">
        <p class="text-black text-lg text-center mb-6">${instruction}</p>
        <div class="addons-modal__variant-picker-list grid grid-cols-1 w-full gap-2">
          ${rowsHtml}
        </div>
      </div>
    `
    }

    const modalFooter = modal?.querySelector('.reusable-modal__footer')
    if (modalFooter) {
      modalFooter.innerHTML = `
      <button type="button" class="addons-modal__variant-confirm button button-primary w-full relative disabled:opacity-50 disabled:cursor-not-allowed">
        <span class="addons-modal__variant-confirm-text">${window.Tentbox?.variantSelection?.select_variant || 'Select variant'}</span>
      </button>
    `
    }

    if (modal) {
      this.initializeAddonVariantModal(modal, card, productData, variants)
    }
  }

  initializeAddonVariantModal (modal, card, productData, variants) {
    const button = modal.querySelector('.addons-modal__variant-confirm')
    const confirmText = modal.querySelector('.addons-modal__variant-confirm-text')
    const rowButtons = modal.querySelectorAll('.addons-modal__variant-picker-row')
    if (!button || !confirmText) return

    let selectedVariant = null
    const confirmLabel = window.TentBox?.addonsModal?.confirmVariantText || 'Continue'

    button.disabled = true
    confirmText.textContent = window.Tentbox?.variantSelection?.select_variant || 'Select variant'

    rowButtons.forEach((row) => {
      row.addEventListener('click', () => {
        if (row.disabled) return
        const id = parseInt(row.dataset.variantId, 10)
        selectedVariant = variants.find(v => Number(v.id) === id)
        modal.querySelectorAll('.addons-modal__variant-picker-row').forEach((r) => {
          r.classList.remove('addons-modal__variant-picker-row--selected')
        })
        row.classList.add('addons-modal__variant-picker-row--selected')
        if (selectedVariant && selectedVariant.available) {
          button.disabled = false
          confirmText.textContent = confirmLabel
        }
      })
    })

    button.addEventListener('click', () => {
      if (button.disabled || !selectedVariant || !selectedVariant.available) return
      this.handleVariantSelection(productData, selectedVariant, card)
      window.dispatchEvent(new CustomEvent('close:modal'))
    })
  }

  handleVariantSelection (productData, variant, card) {
    const normalized = {
      id: variant.id,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice ?? variant.compare_at_price ?? 0,
    }
    productData.selectedVariant = normalized

    const priceRow = card.querySelector('[data-price-row]')
    const comparePriceEl = priceRow.querySelector('[data-compare-price]')
    const priceEl = priceRow.querySelector('[data-price]')
    const priceDisplayRow = priceRow.querySelector('.price-display-wrapper__row')
    const currentBlock = priceDisplayRow?.querySelector('.price-display-wrapper__current')

    const compareAt = normalized.compareAtPrice

    if (comparePriceEl) {
      if (compareAt && compareAt > normalized.price) {
        comparePriceEl.textContent = this.formatPrice(compareAt)
        comparePriceEl.style.display = ''
      } else {
        comparePriceEl.style.display = 'none'
      }
    } else if (compareAt && compareAt > normalized.price && priceDisplayRow && currentBlock) {
      const comparePrice = document.createElement('div')
      comparePrice.className = 'price-display-wrapper__compare line-through font-normal text-alizarin text-[15px] leading-6 shrink-0'
      comparePrice.dataset.comparePrice = ''
      comparePrice.textContent = this.formatPrice(compareAt)
      currentBlock.insertAdjacentElement('afterend', comparePrice)
    }

    priceEl.textContent = this.formatPrice(normalized.price)

    this.updateFinanceLabel(card, normalized.price)

    const isSelected = card.dataset.selected === 'true'
    const oldVariantId = Array.from(this.selectedProducts.keys()).find(key => this.selectedProducts.get(key).productId === productData.id)

    if (oldVariantId) {
      this.selectedProducts.delete(oldVariantId)
    }

    if (!isSelected) {
      card.dataset.selected = 'true'
      this.updateAddButtonText(card, true)
    }

    this.selectedProducts.set(normalized.id, {
      variantId: normalized.id,
      price: normalized.price,
      productId: productData.id,
    })

    this.updateFooter()
  }

  toggleProductSelection (productData, card) {
    const isSelected = card.dataset.selected === 'true'
    const variantId = productData.selectedVariant.id

    if (isSelected) {
      card.dataset.selected = 'false'
      this.selectedProducts.delete(variantId)
    } else {
      card.dataset.selected = 'true'
      this.selectedProducts.set(variantId, {
        variantId,
        price: productData.selectedVariant.price,
        productId: productData.id,
      })
    }

    this.updateAddButtonText(card, !isSelected)
    this.updateFooter()
  }

  updateAddButtonText (card, isSelected) {
    // No per-card add/remove button in simplified modal; noop
  }

  updateFooter () {
    const selectedCount = this.selectedProducts.size
    const buttonTextTemplate = window.TentBox?.addonsModal?.buttonText || 'Add [count] to cart'
    const singleItemText = window.TentBox?.addonsModal?.singleItemText || '1 item'
    const multipleItemsText = window.TentBox?.addonsModal?.multipleItemsText || '[count] items'

    if (selectedCount === 0) {
      this.addButton.disabled = true
      this.addButton.classList.add('hidden')
      this.addButton.classList.remove('addons-modal__add-button--active')
      this.skipToCartButton.classList.remove('hidden')
    } else {
      const countText = selectedCount === 1 ? singleItemText : multipleItemsText.replace('[count]', selectedCount)
      const buttonText = buttonTextTemplate.replace('[count]', countText)
      this.addButtonText.textContent = buttonText
      this.addButton.disabled = false
      this.addButton.classList.remove('hidden')
      this.addButton.classList.add('addons-modal__add-button--active')
      this.skipToCartButton.classList.add('hidden')
    }
  }

  setSkipButtonLoading (isLoading) {
    if (isLoading) {
      this.skipButton.disabled = true
      this.skipButtonSpinner.classList.remove('hidden')
      this.skipButtonSpinner.classList.add('flex')
      this.skipButtonText.classList.add('opacity-0')
    } else {
      this.skipButton.disabled = false
      this.skipButtonSpinner.classList.add('hidden')
      this.skipButtonSpinner.classList.remove('flex')
      this.skipButtonText.classList.remove('opacity-0')
    }
  }

  setSkipToCartButtonLoading (isLoading) {
    if (isLoading) {
      this.skipToCartButton.disabled = true
      this.skipToCartButton.setAttribute('loading', '')
      this.skipToCartButtonSpinner.classList.remove('hidden')
      this.skipToCartButtonSpinner.classList.add('flex')
      this.skipToCartButtonText.classList.add('opacity-0')
    } else {
      this.skipToCartButton.disabled = false
      this.skipToCartButton.removeAttribute('loading')
      this.skipToCartButtonSpinner.classList.add('hidden')
      this.skipToCartButtonSpinner.classList.remove('flex')
      this.skipToCartButtonText.classList.remove('opacity-0')
    }
  }

  async skipToCart () {
    if (this.isProcessing) return
    this.isProcessing = true

    this.setSkipButtonLoading(true)
    this.setSkipToCartButtonLoading(true)

    try {
      await new Promise(resolve => setTimeout(resolve, 500))

      this.closeModal()

      await new Promise(resolve => setTimeout(resolve, 200))

      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer && this.triggerProductCartResponse) {
        cartDrawer.renderContents(this.triggerProductCartResponse)
      } else if (!cartDrawer) {
        window.location = window.routes.cart_url
      }
    } finally {
      this.isProcessing = false
    }
  }

  skipAndAddTriggerProduct () {
    if (this.isProcessing) return
    this.skipToCart()
  }

  async addSelectedProductsToCart () {
    if (this.isProcessing || this.selectedProducts.size === 0) return
    this.isProcessing = true

    this.addButton.disabled = true
    this.addButton.setAttribute('loading', '')

    try {
      const items = Array.from(this.selectedProducts.values()).map(item => ({
        id: item.variantId,
        quantity: 1,
      }))

      await this.addToCart(items)
    } catch (error) {
      this.addButton.disabled = false
      this.addButton.removeAttribute('loading')
      this.isProcessing = false
      throw error
    } finally {
      this.isProcessing = false
      this.closeModal()
    }
  }

  async addToCart (items) {
    try {
      const formData = new FormData()
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id)
        formData.append(`items[${index}][quantity]`, item.quantity)
      })

      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        const sections = cartDrawer.getSectionsToRender().map((section) => section.id)
        sections.forEach((sectionId) => {
          formData.append('sections[]', sectionId)
        })
        formData.append('sections_url', window.location.pathname)
      }

      const addUrl = `${routes.cart_add_url}.js`

      const response = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Failed to add to cart:', errorText)
        throw new Error('Failed to add to cart')
      }

      let data = await response.json()

      if (!data.sections || data.sections === null) {
        try {
          const [drawerRes, bubbleRes] = await Promise.all([
            fetch(`${window.location.pathname}?section_id=cart-drawer`),
            fetch(`${window.location.pathname}?section_id=cart-icon-bubble`),
          ])
          const sections = {}
          if (drawerRes.ok) sections['cart-drawer'] = await drawerRes.text()
          if (bubbleRes.ok) sections['cart-icon-bubble'] = await bubbleRes.text()
          if (Object.keys(sections).length) {
            data = { ...data, sections }
          }
        } catch (e) {}
      }

      if (cartDrawer) {
        cartDrawer.renderContents(data)
      } else {
        window.location = window.routes.cart_url
      }

      window.dispatchEvent(new CustomEvent('cart:updated', { detail: data }))
    } catch (error) {
      console.error('Error adding to cart:', error)
      alert('Failed to add items to cart. Please try again.')
    }
  }

  calculateFinance (priceInCents) {
    const terms = window.TentBox?.addonsModal?.financeTerms || []
    if (terms.length === 0 || !window.FinanceUtils) return null

    const monthlyPayment = window.FinanceUtils.calculateMinMonthlyPayment(priceInCents, terms)
    if (monthlyPayment <= 0) return null

    const rounded = Math.ceil(monthlyPayment)
    return this.formatPrice(rounded * 100)
  }

  formatPrice (priceInCents) {
    if (window.FinanceUtils?.formatMoneyFromCents) {
      return window.FinanceUtils.formatMoneyFromCents(priceInCents)
    }
    const priceNum = parseFloat(priceInCents) / 100
    const formattedNum = priceNum % 1 === 0 ? priceNum.toFixed(0) : priceNum.toFixed(2).replace(/\.?0+$/, '')
    return `£${formattedNum}`
  }

  initScrollFader () {
    if (!this.bodyWrapper || !this.fader) return

    if (this.bottomSentinel && 'IntersectionObserver' in window) {
      const { scrollHeight, clientHeight } = this.bodyWrapper
      const isScrollable = scrollHeight > clientHeight
      if (!isScrollable) {
        this.fader.classList.remove('show')
      } else {
        this.fader.classList.add('show')
      }

      this.faderObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        if (entry.isIntersecting) {
          this.fader.classList.remove('show')
        } else if (this.bodyWrapper.scrollHeight > this.bodyWrapper.clientHeight) {
          this.fader.classList.add('show')
        } else {
          this.fader.classList.remove('show')
        }
      }, {
        root: this.bodyWrapper,
        threshold: 0.01,
      })

      this.faderObserver.observe(this.bottomSentinel)
      return
    }

    this.updateScrollFader()
    this.scrollHandler = this.throttle(() => {
      this.updateScrollFader()
    }, 100)
    this.bodyWrapper.addEventListener('scroll', this.scrollHandler)
  }

  updateScrollFader () {
    if (!this.bodyWrapper || !this.fader) return

    const { scrollTop, scrollHeight, clientHeight } = this.bodyWrapper
    const isScrollable = scrollHeight > clientHeight
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
    const isNearBottom = Math.ceil(distanceFromBottom) <= 5

    if (isScrollable && !isNearBottom) {
      this.fader.classList.add('show')
    } else {
      this.fader.classList.remove('show')
    }
  }

  cleanupScrollFader () {
    if (this.faderObserver) {
      try { this.faderObserver.disconnect() } catch (e) {}
      this.faderObserver = null
    }
    if (this.bodyWrapper && this.scrollHandler) {
      this.bodyWrapper.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }

    if (this.fader) {
      this.fader.classList.remove('show')
    }
  }

  throttle (func, limit) {
    let inThrottle
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => {
          inThrottle = false
        }, limit)
      }
    }
  }
}

if (!customElements.get('addons-modal')) {
  customElements.define('addons-modal', AddonsModal)
}
